import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OnboardingProcess } from "@/types/lifecycle";
import { UserPlus, CheckCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

export function OnboardingWorkflows() {
  const [onboardings, setOnboardings] = useKV<OnboardingProcess[]>("lifecycle-onboarding", [
    {
      id: "onb-1",
      employeeId: "emp-101",
      employeeName: "Alice Johnson",
      department: "Engineering",
      startDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: "Pre-boarding",
      progress: 30,
      tasks: [
        { id: "t1", title: "Sign Contract", assignedTo: "emp-101", dueDate: "2026-02-01", status: "Completed", category: "HR" },
        { id: "t2", title: "Laptop Provisioning", assignedTo: "IT", dueDate: "2026-02-05", status: "Pending", category: "IT" },
      ]
    }
  ]);

  const handleCompleteTask = (processId: string, taskId: string) => {
    const updated = onboardings.map(proc => {
      if (proc.id === processId) {
        const newTasks = proc.tasks.map(t => t.id === taskId ? { ...t, status: 'Completed' } as any : t);
        const progress = Math.round((newTasks.filter(t => t.status === 'Completed').length / newTasks.length) * 100);
        return { ...proc, tasks: newTasks, progress };
      }
      return proc;
    });
    setOnboardings(updated);
    toast.success("Task marked as completed");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Active Onboarding</h3>
        <Button>
          <UserPlus className="mr-2 h-4 w-4" /> Start Onboarding
        </Button>
      </div>

      <div className="grid gap-4">
        {onboardings.map((process) => (
          <Card key={process.id}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>{process.employeeName}</CardTitle>
                  <CardDescription>{process.department} • Starts {new Date(process.startDate).toLocaleDateString()}</CardDescription>
                </div>
                <Badge variant={process.status === 'Pre-boarding' ? 'secondary' : 'default'}>
                  {process.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progress</span>
                    <span>{process.progress}%</span>
                  </div>
                  <Progress value={process.progress} />
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Pending Tasks</h4>
                  {process.tasks.map(task => (
                    <div key={task.id} className="flex items-center justify-between p-2 border rounded bg-slate-50 dark:bg-slate-900/50">
                      <div className="flex items-center gap-2">
                        {task.status === 'Completed' ? (
                          <CheckCircle className="text-green-500 w-5 h-5" />
                        ) : (
                          <Clock className="text-amber-500 w-5 h-5" />
                        )}
                        <span className={task.status === 'Completed' ? 'line-through text-slate-400' : ''}>
                          {task.title}
                        </span>
                        <Badge variant="outline" className="text-xs">{task.category}</Badge>
                      </div>
                      {task.status !== 'Completed' && (
                        <Button size="sm" variant="ghost" onClick={() => handleCompleteTask(process.id, task.id)}>
                          Mark Done
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
